import React from 'react';

import { cn } from '@/design-system/lib/utils';
import { PopoverContent } from '@/design-system/popover';

/**
 * The "Filter (N)" popover body shared by every list page (shipments,
 * invoices, dispatches, inbound, delivery, workflows, UJP masters).
 *
 * A thin wrapper over the kit `PopoverContent` that fixes one bug for all of
 * them: the popper is `position: fixed`, so on a short viewport — a laptop
 * with the page zoomed, a phone, or a list with 0 rows that never scrolls —
 * a panel taller than the screen was simply cut off and its Apply button
 * unreachable. This caps the panel at Radix's available height
 * (`--radix-popover-content-available-height`), keeps it inside the viewport
 * width, and lays the children out as a column so a body marked
 * `min-h-0 flex-1 overflow-y-auto` is the only part that shrinks and scrolls;
 * the header and footer keep their size.
 *
 * It also absorbs the shared `onInteractOutside` guard: clicks into a
 * body-portaled SearchSelect menu must not close the popover.
 *
 * Usage (markup inside stays the page's own):
 *
 *   <FilterPopoverContent aria-label="Filter shipments" className="w-[360px]">
 *     <div className="shrink-0 … border-b">header</div>
 *     <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">fields</div>
 *     <div className="shrink-0 … border-t">footer</div>
 *   </FilterPopoverContent>
 */
export const FilterPopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverContent>,
  React.ComponentPropsWithoutRef<typeof PopoverContent>
>(
  (
    {
      className,
      align = 'end',
      collisionPadding = 8,
      onInteractOutside,
      ...props
    },
    ref
  ) => (
    <PopoverContent
      ref={ref}
      align={align}
      collisionPadding={collisionPadding}
      data-testid="filter-popover"
      className={cn(
        'flex w-[360px] max-w-[calc(100vw-1rem)] flex-col p-0',
        'max-h-(--radix-popover-content-available-height)',
        className
      )}
      onInteractOutside={(e) => {
        const t = e.target as HTMLElement | null;
        if (t?.closest?.('[data-searchable-dropdown-portal]')) {
          e.preventDefault();
          return;
        }
        onInteractOutside?.(e);
      }}
      {...props}
    />
  )
);
FilterPopoverContent.displayName = 'FilterPopoverContent';

/** The class set a panel's scrolling field list needs inside `FilterPopoverContent`. */
export const FILTER_POPOVER_BODY_CLS = 'min-h-0 flex-1 overflow-y-auto';

export default FilterPopoverContent;
